import { Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { getDashboardRewardsLoyalty } from "../../repository/data.repository";
import { DashboardRewardsLoyalty } from "../../repository/data.default";
import {
  ALARM_RED,
  ChartColorScale,
  NEUTRAL_GREY,
  OK_GREEN,
  renderCategoryColumn,
  renderDualTrend,
  renderMetricChart,
  renderRateBar,
  renderShareRing,
} from "./MetricChartRender";
import { InsightChartCard, InsightStrip } from "./InsightCards";
import { pickBucket } from "./AnalyticsRange";
import { DashboardSectionScope } from "./DashboardSection";

// Known redemption workflow statuses get meaningful colours (Pending = red: it's the queue that
// needs working); anything else falls back to a neutral palette.
const STATUS_COLORS: { [status: string]: string } = {
  Pending: ALARM_RED,
  Approved: OK_GREEN,
  Rejected: NEUTRAL_GREY,
};
const STATUS_FALLBACK_COLORS = ["#1677ff", "#13c2c2", "#722ed1", "#eb2f96", "#a0d911"];

// Rewards & loyalty from one server-side rollup: the redemption queue health (pending volume,
// waiting time, approval behaviour), the points economy over time, which rewards draw demand,
// and how much of the registered population the programme actually reaches.
function RewardsLoyaltyCard(props: DashboardSectionScope) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rewards, setRewards] = useState<DashboardRewardsLoyalty | undefined>();

  const from = props.range?.[0];
  const to = props.range?.[1];
  const bucket = pickBucket(from, to);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getDashboardRewardsLoyalty({
      companyUid: props.companyUid,
      departmentUid: props.departmentUid,
      from,
      to,
      bucket,
    })
      .then((data) => {
        if (cancelled) return;
        setRewards(data);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [props.companyUid, props.departmentUid, from, to, bucket]);

  const pending = rewards?.pendingRedemptions ?? 0;

  const economy = (rewards?.economy ?? []).flatMap((p) => [
    { date: dayjs(p.date).format("YYYY-MM-DD"), type: "Earned", value: p.earned },
    { date: dayjs(p.date).format("YYYY-MM-DD"), type: "Spent", value: p.spent },
  ]);
  const queueAging = (rewards?.queueAging ?? []).map((q) => ({
    type: q.band,
    value: q.count,
  }));
  // Named top rewards only — the share ring appends the honest "Other" remainder itself.
  const topRewards = (rewards?.topRewards ?? []).map((r) => ({
    type: r.label,
    value: r.count,
  }));
  const participation = (rewards?.participation ?? []).map((p) => ({
    label: p.label,
    rate: p.rate,
  }));

  // Pending leads the status chart (it's the actionable slice); the rest keep delivered order.
  const statuses = [...(rewards?.statusBreakdown ?? [])].sort(
    (a, b) => (a.status === "Pending" ? -1 : 0) - (b.status === "Pending" ? -1 : 0),
  );
  const statusData = statuses.map((s) => ({ type: s.status, value: s.count }));
  const statusColors: ChartColorScale = {
    domain: statusData.map((s) => s.type),
    range: statusData.map(
      (s, i) =>
        STATUS_COLORS[s.type] ??
        STATUS_FALLBACK_COLORS[i % STATUS_FALLBACK_COLORS.length],
    ),
  };

  const activeBucket = rewards?.bucket ?? bucket;
  const participationTitle =
    (rewards?.groupBy ?? (props.companyUid ? "department" : "company")) ===
    "department"
      ? "Loyalty participation by department"
      : "Loyalty participation by company";

  return (
    <div className="flex w-full flex-col gap-3">
      <InsightStrip
        items={[
          pending > 0 ? (
            <Typography.Text type="warning">
              {pending.toLocaleString()} pending redemptions
            </Typography.Text>
          ) : (
            "0 pending redemptions"
          ),
          `${rewards?.approvalRate ?? 0}% approval`,
          `${rewards?.avgDecisionDays ?? 0}d decision time`,
          `${(rewards?.pointsEarned ?? 0).toLocaleString()} earned / ${(rewards?.pointsSpent ?? 0).toLocaleString()} spent`,
          `${(rewards?.outstandingBalance ?? 0).toLocaleString()} outstanding`,
        ]}
      />
      <div className="flex flex-row flex-wrap gap-3">
        <InsightChartCard
          title="Points economy"
          subtitle={`Points earned vs spent, by ${activeBucket}`}
          loading={loading}
          error={error}
        >
          {renderDualTrend(economy, "Points", 260)}
        </InsightChartCard>
        <InsightChartCard
          title="Pending queue age"
          subtitle="Points are debited at request — long waits hit users directly"
          loading={loading}
          error={error}
        >
          {renderMetricChart("column", {}, queueAging, "Requests", 260)}
        </InsightChartCard>
        <InsightChartCard
          title="Top rewards"
          subtitle="Share of redemptions — top 10 named, the rest as Other"
          loading={loading}
          error={error}
        >
          {renderShareRing(topRewards, rewards?.totalRedemptions, 260)}
        </InsightChartCard>
        <InsightChartCard
          title={participationTitle}
          subtitle={`Top 10 of ${rewards?.participationTotalGroups ?? 0} — share of registered users who have earned points`}
          loading={loading}
          error={error}
        >
          {renderRateBar(participation, 260)}
        </InsightChartCard>
        <InsightChartCard
          title="Redemptions by status"
          subtitle="The pending queue is the actionable slice"
          loading={loading}
          error={error}
        >
          {renderCategoryColumn(statusData, "Requests", statusColors, 260)}
        </InsightChartCard>
      </div>
    </div>
  );
}

export default RewardsLoyaltyCard;
